import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { FunctionComponent } from 'react';

import { EMAIL_RESEND_COOLDOWN_SECONDS } from '../../../../../shared/MfaManager/consts';
import { BannerAttention, BannerSize, BannerStyle } from '../../../../../typings/enums';
import useInitEmailSetup from '../../../../hooks/api/auth/mfa/useInitEmailSetup';
import useResendEmailSetupCode from '../../../../hooks/api/auth/mfa/useResendEmailSetupCode';
import useVerifyEmailSetup from '../../../../hooks/api/auth/mfa/useVerifyEmailSetup';
import { useResendCooldown } from '../../../../hooks/useResendCooldown';
import Banner from '../../../generic/Banner';
import Button from '../../../generic/Button/Button';
import { BUTTON_TYPE } from '../../../generic/Button/button-constants';
import Flex from '../../../generic/Flex';
import VerificationCodeInput from '../../../generic/Forms/VerificationCodeInput';
import Loader from '../../../generic/Loader';

type Props = {
  show: boolean;
  onHide: () => void;
};

type SetupStep = 'verify' | 'success';

/**
 * Modal for setting up Email-based MFA.
 * Sends verification code to user's email and confirms setup.
 */
const EmailSetupModal: FunctionComponent<Props> = ({ show, onHide }) => {
  const [step, setStep] = useState<SetupStep>('verify');
  const [verificationCode, setVerificationCode] = useState('');
  // Not started on mount: this modal stays mounted across open/close cycles, so the window opens with the
  // code that `onEnter` mails, not with the first render.
  const resendCooldown = useResendCooldown(EMAIL_RESEND_COOLDOWN_SECONDS);
  // Pulled out because the hook's result object is rebuilt on every tick, so depending on the whole thing
  // below would rebuild the callback each second. `reset` itself is stable.
  const { reset: resetResendCooldown } = resendCooldown;

  const initMutation = useInitEmailSetup();
  const verifyMutation = useVerifyEmailSetup();
  const resendMutation = useResendEmailSetupCode();

  // Identifies one open/close cycle. Sends resolve asynchronously, and this component stays mounted between
  // cycles, so a request in flight when the user closes would otherwise land on a closed modal — restarting a
  // countdown after `handleClose` cleared it, and carrying one session's window into the next.
  const sessionRef = useRef(0);
  const isCurrentSession = useCallback((session: number) => session === sessionRef.current, []);
  const invalidateSession = useCallback(() => {
    sessionRef.current += 1;
    resetResendCooldown();
  }, [resetResendCooldown]);

  // Closing does not always run `handleClose`: the parent owns `show`, so it can close the modal without the
  // Cancel button, a backdrop dismiss or Escape ever firing `onHide`. Retire the session on that transition
  // too, otherwise a send still in flight survives into the next time the modal opens.
  useEffect(() => {
    if (!show) {
      invalidateSession();
    }
  }, [show, invalidateSession]);

  const handleModalShow = async () => {
    // Send initial code when modal opens
    setStep('verify');
    setVerificationCode('');
    const session = sessionRef.current;
    try {
      await initMutation.mutateAsync();
      if (!isCurrentSession(session)) {
        return;
      }
      // Opening the modal mails a code, which starts the server resend window (MfaManager.generateEmailCode
      // rejects anything sooner), so the button must not be offered for a request the API would refuse.
      resendCooldown.start();
    } catch (error) {
      // Error toast handled by useToastedMutation
      if (isCurrentSession(session)) {
        onHide();
      }
    }
  };

  const handleVerify = async (codeValue?: string) => {
    // Use provided code value or fall back to state (for button clicks)
    const code = codeValue || verificationCode;

    // Validate code length before sending
    if (code.length !== 6) {
      return;
    }

    try {
      await verifyMutation.mutateAsync({ code });
      // Success toast handled by useToastedMutation
      setStep('success');
    } catch (error) {
      // Error toast handled by useToastedMutation
      // Clear the input on failure so user can try again
      setVerificationCode('');
    }
  };

  const handleResend = async () => {
    if (resendCooldown.isCoolingDown || resendMutation.isPending) {
      return;
    }

    const session = sessionRef.current;
    try {
      await resendMutation.mutateAsync();
      if (isCurrentSession(session)) {
        resendCooldown.start();
      }
    } catch (error) {
      // Error toast handled by useToastedMutation
    }
  };

  const handleClose = () => {
    // Retire this session first, so anything still in flight cannot reopen the window we are about to clear.
    invalidateSession();
    setStep('verify');
    setVerificationCode('');
    onHide();
  };

  const isLoading = initMutation.isPending || verifyMutation.isPending || resendMutation.isPending;

  return (
    <Modal show={show} onHide={handleClose} onEnter={handleModalShow}>
      <Modal.Header closeButton>
        <Modal.Title>Set Up Email Verification</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && <Loader />}

        {!isLoading && step === 'verify' && (
          <div>
            <Banner
              body="A 6-digit verification code has been sent to your email address."
              bannerStyle={BannerStyle.Info}
              bannerSize={BannerSize.Small}
              bannerAttention={BannerAttention.Low}
              title="Enter the verification code from your email to complete setup."
            />

            <div className="margin-top-lg margin-bottom-lg">
              <VerificationCodeInput
                length={6}
                value={verificationCode}
                onChange={setVerificationCode}
                onComplete={handleVerify}
                disabled={isLoading}
              />
            </div>
            <div className="text-center">
              <Button
                type={BUTTON_TYPE.TEXT}
                onClick={handleResend}
                isDisabled={resendCooldown.isCoolingDown}
              >
                {resendCooldown.isCoolingDown
                  ? `Resend code (${resendCooldown.secondsRemaining}s)`
                  : 'Resend code'}
              </Button>
            </div>
          </div>
        )}

        {!isLoading && step === 'success' && (
          <Banner
            body=""
            bannerStyle={BannerStyle.Success}
            bannerSize={BannerSize.Small}
            bannerAttention={BannerAttention.Low}
            title="Email MFA setup complete! Your account is now more secure."
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === 'verify' && (
          <Flex gap="4px" justify="flex-end">
            <Button type={BUTTON_TYPE.OUTLINE} onClick={handleClose} isDisabled={isLoading}>
              Cancel
            </Button>
            <Button
              type={BUTTON_TYPE.PRIMARY}
              onClick={() => handleVerify()}
              isDisabled={isLoading || verificationCode.length !== 6}
            >
              Verify and Complete Setup
            </Button>
          </Flex>
        )}
        {step === 'success' && (
          <Button type={BUTTON_TYPE.PRIMARY} onClick={handleClose}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default EmailSetupModal;
